/**
 * 工作流辅助函数
 */

import type { WorkflowConfig } from '@/hooks'
import type { WorkflowStage, TrackStatus } from '@company/shared'

/**
 * 获取启用的阶段列表
 */
export function getEnabledStages(config: WorkflowConfig): WorkflowStage[] {
  const stages: WorkflowStage[] = ['created']

  if (config.productDiscussionEnabled) {
    stages.push('product_discussion')
  }

  if (config.uiDesignEnabled) {
    stages.push('ui_design', 'ui_confirmation')
  }

  if (config.developmentConfig === 'frontend_only') {
    stages.push('frontend_development')
    stages.push('code_review')
  } else if (config.developmentConfig === 'backend_only') {
    stages.push('backend_development')
    stages.push('code_review')
  } else if (config.developmentConfig === 'both') {
    stages.push('parallel_development' as WorkflowStage)
  } else {
    stages.push('code_review')
  }

  if (config.opsDeploymentEnabled) {
    stages.push('ops_deployment')
  }

  stages.push('testing', 'product_acceptance', 'done')

  return stages
}

/**
 * 格式化时间显示（相对时间）
 */
export function formatStageTime(isoString: string | null): string {
  if (!isoString) {
    return '-'
  }
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 60) {
    return `${diffMins}分钟前`
  } else if (diffHours < 24) {
    return `${diffHours}小时前`
  } else if (diffDays < 7) {
    return `${diffDays}天前`
  }
  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 判断是否处于并行开发阶段
 */
export function isInParallelDevelopment(currentStage: WorkflowStage): boolean {
  return currentStage === 'frontend_development' || currentStage === 'backend_development'
}

/**
 * 判断轨道是否已完成
 */
export function isTrackDone(status: TrackStatus): boolean {
  return status === 'approved' || status === 'skipped'
}

/**
 * 判断是否可以回退轨道（从测试或验收阶段）
 */
export function canRollbackTracks(config: WorkflowConfig): boolean {
  return (
    config.developmentConfig === 'both' &&
    (config.currentStage === 'testing' || config.currentStage === 'product_acceptance')
  )
}

/**
 * 获取当前阶段索引
 */
export function getCurrentStageIndex(
  config: WorkflowConfig,
  enabledStages: WorkflowStage[]
): number {
  let currentIndex = enabledStages.indexOf(config.currentStage)

  if (currentIndex === -1 && isInParallelDevelopment(config.currentStage)) {
    const parallelDevIndex = enabledStages.indexOf('parallel_development' as WorkflowStage)
    if (parallelDevIndex >= 0) {
      currentIndex = parallelDevIndex
    }
  }

  return currentIndex
}

/**
 * 获取可回退的阶段列表
 */
export function getRollbackStages(
  config: WorkflowConfig,
  enabledStages: WorkflowStage[]
): WorkflowStage[] {
  const currentIndex = getCurrentStageIndex(config, enabledStages)
  return enabledStages.slice(0, currentIndex)
}
